import { useState } from "react";
import axios from "axios";
import { z } from "zod";
import { createTenderSchema } from "../pages/createTender/schema/tender-schema";
import { createTenderQuotationSchema } from "../pages/vuetender/components/tenderQuotationSchema";
import { getData } from "@/lib/utils";
import api from "@/store/api";
import { useDispatch } from "react-redux";
import { getCategories } from "@/store/authSlice";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";

const useOffer = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const TenderSchema = createTenderSchema(t as (key: string) => string);
  const tenderQuotationSchema = createTenderQuotationSchema(
    t as (key: string) => string
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveingLoading, setisSaveingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const createOffer = async (values: z.infer<typeof TenderSchema>) => {
    setisSaveingLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;

      // Create FormData for file uploads
      const formData = new FormData();

      // Add basic form fields
      formData.append("Paylater", values.Paylater || "No");
      formData.append("title", values.title);
      if (values.value) {
        formData.append("value", values.value.toString());
      }
      formData.append("endDate", values.endDate);
      formData.append("openingDate", values.openingDate);
      formData.append("type", values.type);

      formData.append("addressId", values.addressId || "");

      // Add products array as JSON string
      if (values.products && values.products.length > 0) {
        formData.append(
          "products",
          JSON.stringify(
            values.products.map((item) => ({
              item: item.item,
              quantity: item.quantity,
              unit: item.unit,
              notice: item.notice,
            }))
          )
        );
      }

      // Add TenderNr if available
      if (values.TenderNr) {
        formData.append("TenderNr", values.TenderNr.toString());
      }

      // Add file uploads
      if (values.fileId instanceof File) {
        formData.append("fileId", values.fileId);
      }

      // Handle product attachments
      if (values.products && values.products.length > 0) {
        values.products.forEach((product, index) => {
          if (product.attachment instanceof File) {
            formData.append(`productAttachments[${index}]`, product.attachment);
          }
        });
      }

      // Add other optional fields
      if (values.companyId) {
        formData.append("companyId", values.companyId);
      }

      if (values.categoriesIds && values.categoriesIds.length > 0) {
        formData.append("categoriesIds", JSON.stringify(values.categoriesIds));
      }

      if (values.region) {
        formData.append("region", values.region);
      }

      if (values.city) {
        formData.append("city", values.city);
      }

      if (values.status) {
        formData.append("status", values.status);
      }

      const response = await api.post("/web/tenders/upsert", formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
          // Don't set Content-Type for FormData, let the browser set it with boundary
        },
      });
      setisSaveingLoading(false);
      return response.data;
    } catch (err) {
      setisSaveingLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.creatingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const updateOfferd = async (values: z.infer<typeof TenderSchema>) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;

      // Create FormData for file uploads
      const formData = new FormData();

      // Add basic form fields
      formData.append("Paylater", values.Paylater || "No");
      formData.append("title", values.title);
      if (values.value) {
        formData.append("value", values.value.toString());
      }
      formData.append("endDate", values.endDate);
      formData.append("openingDate", values.openingDate);
      formData.append("type", values.type);
      formData.append("addressId", values.addressId || "");

      // Add products array as JSON string
      if (values.products && values.products.length > 0) {
        formData.append(
          "products",
          JSON.stringify(
            values.products.map((item) => ({
              item: item.item,
              quantity: item.quantity,
              unit: item.unit,
              notice: item.notice,
            }))
          )
        );
      }

      // Add TenderNr if available
      if (values.TenderNr) {
        formData.append("TenderNr", values.TenderNr.toString());
      }

      // Add file uploads
      if (values.fileId instanceof File) {
        formData.append("fileId", values.fileId);
      }

      // Handle product attachments
      if (values.products && values.products.length > 0) {
        values.products.forEach((product, index) => {
          if (product.attachment instanceof File) {
            formData.append(`productAttachments[${index}]`, product.attachment);
          }
        });
      }

      // Add other optional fields
      if (values.companyId) {
        formData.append("companyId", values.companyId);
      }

      if (values.categoriesIds && values.categoriesIds.length > 0) {
        formData.append("categoriesIds", JSON.stringify(values.categoriesIds));
      }

      if (values.region) {
        formData.append("region", values.region);
      }

      if (values.city) {
        formData.append("city", values.city);
      }

      if (values.status) {
        formData.append("status", values.status);
      }

      if (values.id) {
        formData.append("id", values.id);
      }

      const response = await api.post("/web/tenders/upsert", formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          // Don't set Content-Type for FormData, let the browser set it with boundary
        },
      });
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.updatingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const sendOffer = async (id: string, status: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.post(
        "/web/tenders/send",
        { id, status },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      showToast(t("Alert.OfferSubmitted"), "success");

      window.location.reload();

      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("Alert.OfferSendError"));
        showToast(
          err.response?.data?.message || t("Alert.OfferSendError"),
          "warning"
        );
      } else {
        setError(t("Alert.UnexpectedError"));
        showToast(t("Alert.UnexpectedError"), "warning");
      }
      throw err;
    }
  };

  const updateOffer = async (
    id: string,
    values: z.infer<typeof TenderSchema>
  ) => {
    setisSaveingLoading(true);
    setError(null);
    try {
      const response = await api.put(`/api/offers/${id}`, values);
      setisSaveingLoading(false);
      return response.data;
    } catch (err) {
      setisSaveingLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.offerUpdateFailed")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  const getOffer = async (id: string, userId?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/web/tenders/get?id=${id}${userId ? `&userId=${userId}` : ""}`
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(err.response?.data?.message || t("alerts.error.fetchOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const deleteOffer = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const Token = getData("tokens")?.accessToken;

      await api.delete(`/web/tenders/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });

      setIsLoading(false);
      return "Offer deleted successfully";
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.deletingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getOffers = async (
    Status: String,
    search?: string,
    categories?: string[] | string
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/web/tenders/getAll?pageSize=10&pageNumber=1&withPaging=false&status=${Status}${
          search ? `&search=${search}` : ""
        }${categories ? `&categoriesIds=${categories}` : ""}`
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingOffers")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getMyOffers = async (search?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/tenders/getMy?pageSize=10&pageNumber=1&withPaging=false${
          search ? `&search=${search}` : ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          // Offer not found, return null instead of throwing an error
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingOffers")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getcategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/web/categories/getAll?pageSize=10&pageNumber=1&withPaging=false`
      );
      setIsLoading(false);
      dispatch(getCategories(response.data.data));
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCategories")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getParentCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/categories/getAll?pageSize=10&pageNumber=1&withPaging=false&parentId=null`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      dispatch(getCategories(response.data.data));
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCategories")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getcompanies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/companies/getAll?pageSize=10&pageNumber=1&withPaging=false`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCompanies")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const sendOffertRequest = async (
    values: z.infer<typeof tenderQuotationSchema>
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const { customDeliveryMethod, ...filteredValues } = values;
      const response = await api.post(
        "/web/tenderQuotations/upsert",
        filteredValues,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const refuseQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/cancelled`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.refusingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const acceptQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/accept`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.acceptingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const PaymentRequestQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/pay/request`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || "Error updating offer");
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const SendRequestQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/send/request`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const SendQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/send`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const ReceiveQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/receive`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.receivingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  return {
    refuseQuatation,
    sendOffer,
    ReceiveQuatation,
    SendQuatation,
    SendRequestQuatation,
    PaymentRequestQuatation,
    acceptQuatation,
    createOffer,
    deleteOffer,
    updateOfferd,
    updateOffer,
    getOffer,
    getOffers,
    getcategories,
    getcompanies,
    sendOffertRequest,
    getMyOffers,
    getParentCategories,
    isSaveingLoading,
    isLoading,
    error,
  };
};

export default useOffer;
